import React, { useEffect, useState } from "react";
import { GetPlanListApi } from "../../api/request/license";
import {
  AddLicenseApi,
  EditLicenseApi,
  ChangePlanStatus,
  DeletePlan,
} from "../../api/request/plan";
import ModalComponent from "../../components/global/modals/ModalComponent";
import SkeletonTable from "../../components/table/SkeletonTable";
import planColumns from "../../columns/plan/planColumns";
import { Form, Input, InputNumber, Switch } from "antd";
import moment from "moment";
import Edit from "../../assets/Edit";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import LoadableButton from "../../components/button/LoadableButton";
import Plus from "../../assets/Plus";
import ConfirmationModal from "../../components/global/modals/ConfirmationModal";
import Delete from "../../assets/Delete";

function Plans() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();

  // ✅ confirmation for status change
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  // ✅ confirmation for delete
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  // ✅ Fetch Plans
  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetPlanListApi();
      setData(
        data?.data?.map((item) => {
          return {
            ...item,
            status: (
              <Switch
                checked={item.status === "active"}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                onChange={(checked) => {
                  setSelectedPlan(item);
                  setNewStatus(checked ? "active" : "inactive");
                  setIsConfirmOpen(true);
                }}
              />
            ),
            created_at: item?.created_at
              ? moment(item?.created_at).format("lll")
              : "--",
          };
        })
      );
    } catch (error) {
      console.error("Error fetching plan list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // ✅ Add/Edit
  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
        ...(editingPlan
          ? {}
          : { status: values.status ? "active" : "inactive" }),
      };

      if (editingPlan) {
        await EditLicenseApi({ id: editingPlan.id, ...payload });
      } else {
        await AddLicenseApi(payload);
      }

      fetchPlans();
      setIsModalOpen(false);
      form.resetFields();
      setEditingPlan(null);
    } catch (error) {
      console.error("Error saving plan:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Confirm Change Status
  const handleConfirmStatus = async () => {
    if (!selectedPlan) return;
    setIsConfirmLoading(true);
    try {
      await ChangePlanStatus({ id: selectedPlan.id, status: newStatus });
      fetchPlans();
      setIsConfirmOpen(false);
      setSelectedPlan(null);
      setNewStatus(null);
    } catch (error) {
      console.error("Error changing status:", error);
    } finally {
      setIsConfirmLoading(false);
    }
  };

  // ✅ Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedPlan) return;
    setIsDeleteLoading(true);
    try {
      await DeletePlan({ id: selectedPlan.id });
      fetchPlans();
      setIsDeleteOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error("Error deleting plan:", error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  // ✅ Open Add
  const openAddModal = () => {
    setEditingPlan(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  // ✅ Open Edit
  const openEditModal = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      name: plan.name,
      total_user: plan.total_user,
      duration: plan.duration,
      amount: plan.amount,
    });
    setIsModalOpen(true);
  };

  return (
    <div>
      <div className="border bg-white rounded-xl py-3">
        <div className="flex items-center justify-between sm:px-4 max-sm:px-2 mb-1">
          <h2 className="text-xl font-semibold">Plans</h2>
          <button
            className="flex items-center gap-2 m-2 px-4 py-2 bg-secondary text-white rounded-md max-sm:px-1 max-sm:py-1"
            onClick={openAddModal}
          >
            <Plus className="w-5 h-5" />
            <span className="max-sm:text-xs">Add Plan</span>
          </button>
        </div>

        {/* ✅ Table */}
        <SkeletonTable
          columns={[
            ...planColumns,
            {
              title: "Actions",
              key: "actions",
              render: (_, record) => (
                <div className="flex gap-2">
                  <GlobalTooltip title="Edit Plan">
                    <button onClick={() => openEditModal(record)}>
                      <Edit />
                    </button>
                  </GlobalTooltip>
                  <GlobalTooltip title="Delete Plan">
                    <button
                      disabled={isDeleteLoading}
                      onClick={() => {
                        setSelectedPlan(record);
                        setIsDeleteOpen(true);
                      }}
                    >
                      <Delete />
                    </button>
                  </GlobalTooltip>
                </div>
              ),
            },
          ]}
          data={data}
          isLoading={isLoading}
          pagination={true}
        />
      </div>

      {/* ✅ Add/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={editingPlan ? "Edit Plan" : "Add Plan"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: true }}
        >
          <Form.Item
            label="Plan Name"
            name="name"
            rules={[{ required: true, message: "Please enter plan name" }]}
          >
            <Input placeholder="Enter plan name" />
          </Form.Item>

          <Form.Item
            label="Total Employees"
            name="total_user"
            rules={[{ required: true, message: "Please enter total users" }]}
          >
            <InputNumber min={1} className="w-full" />
          </Form.Item>

          <Form.Item
            label="Duration"
            name="duration"
            rules={[{ required: true, message: "Please enter duration" }]}
          >
            <Input placeholder="e.g. 12 months" />
          </Form.Item>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[{ required: true, message: "Please enter amount" }]}
          >
            <InputNumber min={0} className="w-full" />
          </Form.Item>

          {/* Only for Add */}
          {!editingPlan && (
            <Form.Item label="Status" name="status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <LoadableButton
              className="btn-primary"
              type="submit"
              lable={editingPlan ? "Update" : "Add"}
              loadingLable="Saving..."
              isLoading={isLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      {/* ✅ Status Change Confirmation */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        type="warning"
        message={`Are you sure you want to set this plan as ${newStatus}?`}
        onConfirm={handleConfirmStatus}
        isLoading={isConfirmLoading}
        loadingLabel="Updating..."
      />

      {/* ✅ Delete Confirmation */}
      <ConfirmationModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        type="error"
        message={`Are you sure you want to delete "${selectedPlan?.name}"?`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
}

export default Plans;
